// src/components/access/StudioAccessGate.tsx
// Track15 Audio Studio access gate (client-side UX gate).
//
// Wraps studio routes. Renders children only when:
//   1. Auth is resolved AND
//   2. A user is signed in AND
//   3. That user's email is in VITE_STUDIO_ALLOWED_EMAILS
//
// Otherwise renders NotFoundPage (visually indistinguishable from a true 404),
// satisfying the spec's 404-invisibility doctrine at the SPA level.
//
// Server-side enforcement (edge functions in Slice 2+) is the truth gate.
// This client gate is UX only — it hides the surface, it does not secure data.

import { ReactNode } from "react";

import { useAuth } from "@/context/AuthContext";
import { isStudioAllowed } from "@/features/studio/audio/services/studioAccess";
import NotFoundPage from "@/pages/NotFoundPage";

type StudioAccessGateProps = {
  children: ReactNode;
};

export function StudioAccessGate({ children }: StudioAccessGateProps) {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="mb-4 text-4xl">⏳</div>
          <div className="text-sm text-[var(--text-muted)]">Loading…</div>
        </div>
      </div>
    );
  }

  if (!user || !isStudioAllowed(user.email)) {
    return <NotFoundPage />;
  }

  return <>{children}</>;
}
